import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AddPatientDetails,
  GetPatientById,
  UpdatePatientDetails,
  getGenderDropDown,
} from "../../../utils/apiCalls/patients";
import { GetDeviceAssignedForProfessionalById } from "../../../utils/apiCalls/devices";
import { safeParseSessionStorage } from "../../../utils/storage";
import { useToast } from "../../../utils/ToastContext";

const initialValues = {
  name: "",
  userName: "",
  deviceId: "",
  genderId: "",
  dateOfBirth: "",
  weight: "",
  weightMeasurementType: "",
  height: "",
  heightMeasurementType: "",
  mobileNumber: "",
  email: "",
  password: "",
  educationDegreeId: "",
  raceId: "",
  dataMode: "online",
  dataAcquisitionMode: "1",
  isIntermittentCollection: false,
  activeTime: "",
  sleepTime: "",
  isEventButtonEnabled: true,
  isLivePlotsEnabled: true,
};

// Get Patient Details returns dataAcquisitionMode as a display string;
// Add/Update expect the numeric id back, so map string -> id for the form.
const ACQUISITION_MODE_FROM_STRING = {
  Hz25Regular: "1",
  Hz25Raw: "2",
  Hz100Raw: "3",
};

// Backend enum: None = 0, Online = 1, Offline = 2. None isn't user-selectable
// from the UI toggle; anything unrecognized (including a missing value, or a
// legacy "Both" = 3 from before that option was removed) falls back to Online.
const DATA_MODE_FROM_ACQUISITION_MODE = (mode) => (Number(mode) === 2 ? "offline" : "online");
const ACQUISITION_MODE_FROM_DATA_MODE = (dataMode) => (dataMode === "offline" ? 2 : 1);

const convertHeightToDecimal = (raw) => {
  if (raw == null || raw === "") return null;

  let value = raw;

  // If HeightInput sends an object, try to pick something string-like
  if (typeof raw === "object") {
    if (typeof raw.value === "string" || typeof raw.value === "number") {
      value = raw.value;
    } else if (
      typeof raw.height === "string" ||
      typeof raw.height === "number"
    ) {
      value = raw.height;
    } else if (typeof raw.main === "string" || typeof raw.main === "number") {
      value = raw.main;
    } else {
      return null; // we don't know how to read this object
    }
  }

  if (typeof value === "number") value = String(value);
  if (typeof value !== "string") return null;

  value = value.trim();
  if (!value) return null;

  // If already like 6.7 or 5.11, just send as is
  if (/^\d+(\.\d+)?$/.test(value)) return value;

  // Normalize: remove ", spaces, ft, in, etc.
  let cleaned = value.replace(/"/g, "").replace(/\s+/g, "");

  // Case: 6'7 or 6'7"
  if (cleaned.includes("'")) {
    const [feet, inches = "0"] = cleaned.split("'");
    return `${feet}.${inches}`;
  }

  return value; // safe fallback
};

export default function usePatientForm() {
  const { showToast } = useToast();
  const [errors, setErrors] = useState(initialValues);
  const [formData, setFormData] = useState(initialValues);
  const [isLoading, setIsLoading] = useState(false);
  const [gender, setGender] = useState([]);
  const [device, setDevice] = useState([]);
  const [isDeviceRemoved, setIsDeviceRemoved] = useState(false);
  // Last known-good (server-saved) form values, snapshotted whenever we load
  // or successfully save. Cancel restores from this synchronously instead of
  // re-fetching, so a slow/failed network request can't leave an unsaved
  // edit showing in the read-only view.
  const pristineFormDataRef = useRef(initialValues);
  const location = useLocation();
  const navigate = useNavigate();
  const data = location?.state?.rowData || safeParseSessionStorage("rowData");
  const patientId = data?.id;
  const isEditing = !!patientId;
  const [resetPassword, setResetPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value,
    });
    setErrors({
      ...errors,
      [name]: "",
    });
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleRemoveDevice = () => {
    setFormData((prev) => ({
      ...prev,
      deviceId: "",
      deviceName: "",
    }));
    setIsDeviceRemoved(true);
  };

  const handleDeviceSelect = (e) => {
    const { name, value } = e.target;
    const selected = device.find((d) => String(d.value) === String(value));
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      deviceName: selected?.label || "",
    }));
    setIsDeviceRemoved(false);
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const validateFields = (section) => {
    let errObj = { ...initialValues };

    if (section !== "acquisition") {
      if (!formData.name) {
        errObj.name = "This field is required";
      } else {
        errObj.name = "";
      }

      if (!formData.userName) {
        errObj.userName = "This field is required";
      } else if (!/^.{3,16}$/.test(formData.userName)) {
        errObj.userName = "Minimum 3 and Maximum 16 characters only";
      } else {
        errObj.userName = "";
      }

      if (isEditing && resetPassword) {
        if (!formData.password) {
          errObj.password = "This field is required";
        } else if (formData.password.length < 8) {
          errObj.password = "Password must be at least 8 characters";
        } else {
          errObj.password = "";
        }
      } else if (!isEditing) {
        if (!formData.password) {
          errObj.password = "This field is required";
        } else if (formData.password.length < 8) {
          errObj.password = "Password must be at least 8 characters";
        } else {
          errObj.password = "";
        }
      } else {
        errObj.password = "";
      }

      if (!isEditing && !formData.deviceId) {
        errObj.deviceId = "Please select a device";
      } else {
        errObj.deviceId = "";
      }

      if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
        errObj.email = "Email is invalid";
      } else {
        errObj.email = "";
      }

      if (formData.mobileNumber && !/^\d{7,15}$/.test(formData.mobileNumber)) {
        errObj.mobileNumber = "Enter a valid mobile number";
      } else {
        errObj.mobileNumber = "";
      }

      if (formData.weight && !/^\d+(\.\d+)?$/.test(formData.weight)) {
        errObj.weight = "Enter a valid weight";
      } else {
        errObj.weight = "";
      }

      if (formData.height && !/^\d+(\.\d+)?$|^\d+'\d+"?$/.test(formData.height)) {
        errObj.height = "Enter a valid height";
      } else {
        errObj.height = "";
      }
    }

    // Not user-validated fields; reset so they don't fail the isValid check below.
    errObj.dataMode = "";
    errObj.dataAcquisitionMode = "";
    errObj.isIntermittentCollection = "";
    errObj.activeTime = "";
    errObj.sleepTime = "";
    errObj.isEventButtonEnabled = "";
    errObj.isLivePlotsEnabled = "";

    setErrors(errObj);
    const isValid = Object.values(errObj).every((x) => x === "" || x === null);
    return isValid;
  };

  const onResetPasswordClick = () => {
    if (!resetPassword) {
      // enabling reset: clear the masked value for actual editing
      setResetPassword(true);
      setFormData((prev) => ({ ...prev, password: "" }));
    } else {
      // cancel reset: revert to masked/disabled state
      setResetPassword(false);
      setFormData((prev) => ({ ...prev, password: "" })); // keep actual password empty; UI will show "********"
    }
  };

  // `section` scopes a patient update to one tab's worth of fields so the
  // Profile and Acquisition Settings tabs can each save independently:
  // "basic" | "acquisition" | undefined (both — used by the combined Add/Edit form).
  const handleSubmit = async (onSuccess, section) => {
    setIsLoading(true);
    if (validateFields(section)) {
      const basicDetails = {
        name: formData.name,
        userName: formData.userName,
        deviceId: Number(formData.deviceId) || 0,
        genderId: formData.genderId ? formData.genderId : null,
        dateOfBirth: formData.dateOfBirth,
        weight: formData.weight || null,
        weightMeasurementType: Number(formData.weightMeasurementType) || 1,
        height: convertHeightToDecimal(formData.height),
        heightMeasurementType: Number(formData.heightMeasurementType) || 1,
        mobileNumber: formData.mobileNumber ? formData.mobileNumber : null,
        email: formData.email ? formData.email : null,
        password: formData.password,
        isDeviceRemoved: isDeviceRemoved,
        imageUrl: null,
      };
      const acquisitionSettings = {
        dataAcquisitionMode: Number(formData.dataAcquisitionMode) || 1,
        isIntermittentCollection: !!formData.isIntermittentCollection,
        activeTime: formData.isIntermittentCollection ? (Number(formData.activeTime) || 0) : 0,
        sleepTime: formData.isIntermittentCollection ? (Number(formData.sleepTime) || 0) : 0,
        mode: ACQUISITION_MODE_FROM_DATA_MODE(formData.dataMode),
        isEventButtonEnabled: !!formData.isEventButtonEnabled,
        isLivePlotsEnabled: !!formData.isLivePlotsEnabled,
      };

      if (data?.id) {
        const payload = {
          updatePatientBasicDetails:
            section === "acquisition" ? null : basicDetails,
          acquisitionSettings: section === "basic" ? null : acquisitionSettings,
        };
        try {
          const res = await UpdatePatientDetails(payload, data?.id);
          const { statusCode, message } = res;
          if (statusCode === 200) {
            setIsLoading(false);
            showToast("Data Updated Successfully", "success");

            // Re-fetch patient details from server so state updates
            if (data?.id) {
              await fetchPatientToFormById(data.id);
            }

            if (payload.updatePatientBasicDetails) {
              // Update sessionStorage values in case name or device ID changed
              sessionStorage.setItem("patientName", basicDetails.name);
              if (basicDetails.isDeviceRemoved) {
                sessionStorage.setItem("deviceId", "No device assigned");
              } else {
                const selectedDevice = device.find((d) => Number(d.value) === Number(basicDetails.deviceId));
                if (selectedDevice) {
                  sessionStorage.setItem("deviceId", selectedDevice.label);
                }
              }
              sessionStorage.setItem("rowData", JSON.stringify({
                ...data,
                name: basicDetails.name,
                deviceId: sessionStorage.getItem("deviceId"),
              }));
            }

            if (typeof onSuccess === "function") {
              onSuccess();
            } else {
              navigate("/patient-table");
            }
          } else {
            setIsLoading(false);
            showToast(message, "error");
          }
        } catch (error) {
          setIsLoading(false);
          showToast(error?.message, "error");
        }
      } else {
        const payload = { ...basicDetails, acquisitionSettings };
        try {
          const res = await AddPatientDetails(payload);
          const { statusCode, message } = res;
          if (statusCode === 200) {
            setIsLoading(false);
            showToast(message, "success");
            navigate("/patient-table");
            setIsDeviceRemoved(false);
          } else {
            setIsLoading(false);
            showToast(message, "error");
          }
        } catch (error) {
          setIsLoading(false);
          showToast(error?.message, "error");
        }
      }
    } else {
      setIsLoading(false);
      setIsDeviceRemoved(false);
    }
  };

  const getAllGenderData = async () => {
    try {
      const res = await getGenderDropDown();
      const { data: genderData, statusCode } = res;
      if (statusCode === 200) {
        const formattedData = genderData.map((item) => ({
          value: item.id,
          label: item.genderName,
        }));
        setGender(formattedData);
      }
    } catch (error) {
      console.error("Error fetching gender dropdown:", error?.message);
    }
  };

  const formatDate = useCallback((dateString) => {
    if (dateString) {
      const [day, month, year] = dateString.split("-");
      return `${day}-${month}-${year}`;
    }
    return "";
  }, []);

  const fetchPatientToFormById = useCallback(async (patientId) => {
    try {
      const res = await GetPatientById(patientId);
      const { data: patientData, message, statusCode } = res;
      if (statusCode === 200) {
        const formattedDateOfBirth = formatDate(patientData.dateOfBirth);

        setFormData((prev) => {
          const next = {
            ...prev,
            ...patientData,
            dateOfBirth: formattedDateOfBirth || "",
            height: patientData.height ?? "",
            weight: patientData.weight ?? "",
            mobileNumber: patientData.mobileNumber ?? "",
            email: patientData.email ?? "",
            password: "", // since edit mode disables password
            dataAcquisitionMode:
              ACQUISITION_MODE_FROM_STRING[
                patientData.acquisitionSettings?.dataAcquisitionMode
              ] || "1",
            dataMode: DATA_MODE_FROM_ACQUISITION_MODE(
              patientData.acquisitionSettings?.mode,
            ),
            isIntermittentCollection:
              patientData.acquisitionSettings?.isIntermittentCollection ?? false,
            activeTime: patientData.acquisitionSettings?.activeTime ?? "",
            sleepTime: patientData.acquisitionSettings?.sleepTime ?? "",
            isEventButtonEnabled:
              patientData.acquisitionSettings?.isEventButtonEnabled ?? true,
            isLivePlotsEnabled: patientData.acquisitionSettings?.isLivePlotsEnabled ?? true,
          };
          pristineFormDataRef.current = next;
          return next;
        });
      } else {
        console.error("Error fetching patient by ID:", message);
      }
    } catch (error) {
      console.error("Error fetching patient by ID:", error?.message);
    }
  }, [formatDate]);

  useEffect(() => {
    if (patientId) {
      fetchPatientToFormById(patientId);
    }
  }, [patientId, fetchPatientToFormById]);

  const getDeviceAssignedForProfessionalById = async () => {
    try {
      const res = await GetDeviceAssignedForProfessionalById();
      const { data: deviceData, statusCode } = res;
      if (statusCode === 200) {
        const formattedData = deviceData.map((item) => ({
          value: item.deviceId,
          label: item.deviceName,
        }));
        setDevice(formattedData);
      }
    } catch (error) {
      console.error("Error fetching assigned devices:", error?.message);
    }
  };

  useEffect(() => {
    getAllGenderData();
    getDeviceAssignedForProfessionalById();
  }, []);

  const handleTable = () => {
    navigate("/patient-table");
  };

  // Discards any unsaved edits (Cancel, tab switch) by restoring the last
  // known-good snapshot synchronously - no network round trip, so there's no
  // window where a stale/slow fetch leaves an unsaved edit on screen.
  const resetForm = useCallback(() => {
    setIsDeviceRemoved(false);
    setResetPassword(false);
    setErrors(initialValues);
    setFormData(pristineFormDataRef.current);
  }, []);

  return {
    errors,
    formData,
    isLoading,
    gender,
    device,
    data,
    isEditing,
    resetPassword,
    handleChange,
    handleCheckboxChange,
    handleRemoveDevice,
    handleDeviceSelect,
    onResetPasswordClick,
    handleSubmit,
    handleTable,
    resetForm,
  };
}
